import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { hasAnyWriteToken } from '../utils/writeAuth';
import { playChime, showNotification } from '../utils/notify';

// Owns the support surface end-to-end: Jonathan's presence, the
// support category/employee rosters, the front-desk ticket list with
// its Firestore-backed read state, and the cross-cutting
// UI feedback (chime + desktop notification on rising-edge unread,
// title flash while unread > 0).
//
// Effects owned (9):
//   1. Mount fetch /api/support/categories.
//   2. Presence poll every 15s — extracts Jonathan, respects
//      `availableUntil` expiry.
//   3. Local `availableUntil` expiry timeout (flips status to offline
//      between server polls so the UI doesn't look stale).
//   4. Employees poll every 30s (auth-dep).
//   5. Read-state hydration from Firestore with localStorage fallback
//      + one-shot back-fill POST when Firestore was empty and
//      localStorage has a legacy cursor.
//   6. Tickets poll 10s fast / 30s slow (fast when a support drawer is
//      open). API max limit is 200.
//   7. Rising-edge chime + desktop notification on new admin replies.
//      Baseline ref starts null so the first hydration post-login
//      doesn't blast a chime for the full backlog.
//   8. Title flash while unread > 0.
//   9. One-time removal of a legacy `supportSelectedEmployeeId`
//      localStorage key from pre-refactor builds.
//
// No getSlice() — nothing here is part of POST /api/save.
export function useSupport({
  adminMode,
  frontdeskAuth,
  writeAuthEpoch,
  authHydrated = true,
  supportDrawerOpen,
  ticketsDrawerOpen,
  setTicketsDrawerOpen,
}) {
  const [supportCategories, setSupportCategories] = useState([]);
  const [supportEmployees, setSupportEmployees] = useState([]);
  // NOTE: intentionally NOT persisted across sessions. Front-desk staff
  // should always see "Select your name…" when the drawer opens, so
  // tickets never get mis-attributed to whoever filed one last.
  const [supportSelectedEmployeeId, setSupportSelectedEmployeeId] = useState('');
  const [jonathanStatus, setJonathanStatus] = useState('offline'); // online|busy|offline
  const [jonathanAvailableUntil, setJonathanAvailableUntil] = useState(null); // ISO string | null
  const [supportMyTickets, setSupportMyTickets] = useState([]);
  // Front-desk read cursors: loaded from Firestore via API (shared across
  // devices / incognito). localStorage is only a fallback when the fetch
  // fails and a write-through cache on success.
  const [supportReadStateHydrated, setSupportReadStateHydrated] = useState(false);
  const [supportSeenAt, setSupportSeenAt] = useState(null);
  const [supportTicketSeenMap, setSupportTicketSeenMap] = useState({});

  useEffect(() => {
    let alive = true;
    const fetchCategories = async () => {
      try {
        const res = await fetch('/api/support/categories');
        const j = await res.json();
        if (!alive) return;
        if (res.ok && j && j.ok) setSupportCategories(Array.isArray(j.categories) ? j.categories : []);
      } catch {
        // ignore
      }
    };
    fetchCategories();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    let t = null;
    const fetchPresence = async () => {
      try {
        const res = await fetch('/api/support/presence-list');
        const j = await res.json();
        if (!alive) return;
        if (res.ok && j && j.ok && Array.isArray(j.presence)) {
          const jon = j.presence.find((p) => p.id === 'jonathan' || p.staffId === 'jonathan' || (p.displayName || '').toLowerCase() === 'jonathan');
          if (jon) {
            const hasUntil = jon.availableUntil && !Number.isNaN(Date.parse(jon.availableUntil));
            const expired = hasUntil && new Date(jon.availableUntil).getTime() <= Date.now();
            const effectiveStatus = expired ? 'offline' : (jon.status || 'offline');
            setJonathanStatus(effectiveStatus);
            setJonathanAvailableUntil(hasUntil && !expired ? jon.availableUntil : null);
          }
        }
      } catch {
        // ignore
      }
    };
    fetchPresence();
    // Presence itself can stay on a slower cadence; live-chat uses its own
    // 4s poll when Jonathan is online.
    t = setInterval(fetchPresence, 15000);
    return () => {
      alive = false;
      if (t) clearInterval(t);
    };
  }, []);

  // Local tick to expire availableUntil between server polls.
  useEffect(() => {
    if (!jonathanAvailableUntil) return undefined;
    const expiresAt = Date.parse(jonathanAvailableUntil);
    if (Number.isNaN(expiresAt)) return undefined;
    const delay = Math.max(expiresAt - Date.now(), 0);
    const t = setTimeout(() => {
      setJonathanStatus('offline');
      setJonathanAvailableUntil(null);
    }, delay + 250);
    return () => clearTimeout(t);
  }, [jonathanAvailableUntil]);

  useEffect(() => {
    let alive = true;
    const fetchEmployees = async () => {
      if (!authHydrated) return;
      if (!hasAnyWriteToken()) return;
      try {
        const res = await authFetch('/api/support/employees');
        const j = await res.json();
        if (!alive) return;
        if (res.ok && j && j.ok) setSupportEmployees(Array.isArray(j.employees) ? j.employees : []);
      } catch {
        // ignore
      }
    };
    fetchEmployees();
    const t = setInterval(fetchEmployees, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [writeAuthEpoch, frontdeskAuth, adminMode, authHydrated]);

  // Shared front-desk ticket read state (Firestore) so unread badges and
  // notifications stay aligned across browsers, incognito, and devices.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!authHydrated) return undefined;
    if (!adminMode && !frontdeskAuth) {
      setSupportReadStateHydrated(false);
      setSupportSeenAt(null);
      setSupportTicketSeenMap({});
      return undefined;
    }
    if (!hasAnyWriteToken()) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await authFetch('/api/support/frontdesk-read-state');
        const j = await res.json();
        if (!alive) return;
        if (!res.ok || !j || !j.ok) throw new Error(j?.error || 'read state failed');
        let seen = typeof j.ticketsSeenAt === 'string' && j.ticketsSeenAt ? j.ticketsSeenAt : null;
        let map = j.ticketSeenMap && typeof j.ticketSeenMap === 'object' && !Array.isArray(j.ticketSeenMap)
          ? { ...j.ticketSeenMap }
          : {};
        if (!seen) {
          try {
            const ls = localStorage.getItem('supportTicketsSeenAt');
            if (ls && !Number.isNaN(Date.parse(ls))) {
              const lsIso = new Date(ls).toISOString();
              seen = lsIso;
              authFetch('/api/support/frontdesk-read-state', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ markAll: true, at: lsIso }),
              }).then((r) => r.json()).catch(() => {});
            }
          } catch { /* ignore */ }
        }
        if (!seen) seen = new Date().toISOString();
        try {
          localStorage.setItem('supportTicketsSeenAt', seen);
          if (Object.keys(map).length) localStorage.setItem('supportTicketSeenMap', JSON.stringify(map));
          else localStorage.removeItem('supportTicketSeenMap');
        } catch { /* ignore */ }
        setSupportSeenAt(seen);
        setSupportTicketSeenMap(map);
        setSupportReadStateHydrated(true);
      } catch {
        if (!alive) return;
        try {
          const ls = localStorage.getItem('supportTicketsSeenAt');
          const seen = (ls && !Number.isNaN(Date.parse(ls)) ? new Date(ls) : new Date()).toISOString();
          let map = {};
          const rawMap = localStorage.getItem('supportTicketSeenMap');
          if (rawMap) {
            const parsed = JSON.parse(rawMap);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) map = parsed;
          }
          setSupportSeenAt(seen);
          setSupportTicketSeenMap(map);
        } catch {
          setSupportSeenAt(new Date().toISOString());
          setSupportTicketSeenMap({});
        }
        setSupportReadStateHydrated(true);
      }
    })();
    return () => { alive = false; };
  }, [adminMode, frontdeskAuth, writeAuthEpoch, authHydrated]);

  // Poll tickets for anyone authenticated (admin or front desk): full list
  // so staff can coordinate and follow up on all threads (same as Admin
  // Panel intent). API max limit is 200.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const pollFaster = supportDrawerOpen || ticketsDrawerOpen;
    let alive = true;
    let timer = null;
    const load = async () => {
      try {
        if (!authHydrated) return;
        if (!adminMode && !frontdeskAuth) return;
        if (!hasAnyWriteToken()) return;
        const res = await authFetch('/api/support/tickets?limit=200');
        const j = await res.json();
        if (!alive) return;
        if (res.ok && j && j.ok && Array.isArray(j.tickets)) {
          setSupportMyTickets(j.tickets);
        }
      } catch {
        // ignore
      }
    };
    load();
    timer = setInterval(load, pollFaster ? 10000 : 30000);
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [supportDrawerOpen, ticketsDrawerOpen, adminMode, frontdeskAuth, writeAuthEpoch, authHydrated]);

  const supportUnreadCount = useMemo(() => {
    if (!supportReadStateHydrated) return 0;
    if (!Array.isArray(supportMyTickets) || supportMyTickets.length === 0) return 0;
    const globalSeen = supportSeenAt || new Date().toISOString();
    return supportMyTickets.reduce((n, t) => {
      // Only count tickets where the MOST RECENT activity came from the
      // support side. This prevents the front desk from being alerted
      // about their own replies, and prevents an old admin reply (with
      // hasSupportReply=true forever) from re-triggering "NEW" after
      // the FD replies on top of it.
      if (t.lastAuthorType !== 'support') return n;
      if (!t.hasSupportReply) return n;
      const last = t.lastActivityAt || t.createdAt || '';
      if (!last) return n;
      const seen = supportTicketSeenMap[t.id] || globalSeen;
      return last > seen ? n + 1 : n;
    }, 0);
  }, [supportMyTickets, supportSeenAt, supportTicketSeenMap, supportReadStateHydrated]);

  const handleMarkSupportSeen = useCallback(async () => {
    const now = new Date().toISOString();
    setSupportSeenAt(now);
    setSupportTicketSeenMap({});
    try {
      const res = await authFetch('/api/support/frontdesk-read-state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ markAll: true, at: now }),
      });
      const j = await res.json();
      if (res.ok && j && j.ok) {
        if (typeof j.ticketsSeenAt === 'string' && j.ticketsSeenAt) setSupportSeenAt(j.ticketsSeenAt);
        if (j.ticketSeenMap && typeof j.ticketSeenMap === 'object' && !Array.isArray(j.ticketSeenMap)) {
          setSupportTicketSeenMap(j.ticketSeenMap);
        }
        if (typeof window !== 'undefined') {
          try {
            const persisted = typeof j.ticketsSeenAt === 'string' && j.ticketsSeenAt ? j.ticketsSeenAt : now;
            localStorage.setItem('supportTicketsSeenAt', persisted);
            localStorage.removeItem('supportTicketSeenMap');
          } catch { /* ignore */ }
        }
        return;
      }
    } catch {
      // optimistic UI already applied
    }
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('supportTicketsSeenAt', now);
        localStorage.removeItem('supportTicketSeenMap');
      } catch { /* ignore */ }
    }
  }, []);

  // Fire a desktop notification + soft chime whenever the unread
  // count increases (i.e., a new admin reply just arrived). We only
  // trigger on the rising edge so acknowledging a ticket doesn't
  // silence the chime for the *next* reply. Rate-limited inside
  // playChime() so a burst of replies stays civilized.
  // Baseline ref starts null (same pattern as AdminPanel) so the first
  // snapshot after load does not treat the whole backlog as "new".
  const prevSupportUnreadRef = useRef(null);
  useEffect(() => {
    const prev = prevSupportUnreadRef.current;
    if (prev !== null && supportUnreadCount > prev) {
      playChime();
      const n = supportUnreadCount;
      showNotification(
        `${n} new support repl${n === 1 ? 'y' : 'ies'}`,
        {
          body: 'Jonathan responded in Modality Support. Click to view.',
          tag: 'modality-support-fd',
          onClick: () => setTicketsDrawerOpen(true),
        },
      );
    }
    prevSupportUnreadRef.current = supportUnreadCount;
  }, [supportUnreadCount, setTicketsDrawerOpen]);

  // Flash the browser tab title when there's unread support activity so
  // the front desk notices even when they're in another tab. We restore
  // the original title as soon as everything is caught up.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const originalTitle = 'Slope Wellness | Operations';
    if (supportUnreadCount <= 0) {
      document.title = originalTitle;
      return undefined;
    }
    let flipped = false;
    const alertTitle = `🔔 (${supportUnreadCount > 9 ? '9+' : supportUnreadCount}) Support Reply — ${originalTitle}`;
    document.title = alertTitle;
    const t = setInterval(() => {
      flipped = !flipped;
      document.title = flipped ? originalTitle : alertTitle;
    }, 1500);
    return () => {
      clearInterval(t);
      document.title = originalTitle;
    };
  }, [supportUnreadCount]);

  const handleMarkTicketSeen = useCallback((ticketId) => {
    if (!ticketId) return;
    const now = new Date().toISOString();
    setSupportTicketSeenMap((prev) => {
      const next = { ...prev, [ticketId]: now };
      if (typeof window !== 'undefined') {
        try { localStorage.setItem('supportTicketSeenMap', JSON.stringify(next)); } catch { /* ignore */ }
      }
      return next;
    });
    (async () => {
      try {
        const res = await authFetch('/api/support/frontdesk-read-state', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ markTicket: ticketId, at: now }),
        });
        const j = await res.json();
        if (res.ok && j && j.ok && j.ticketSeenMap && typeof j.ticketSeenMap === 'object' && !Array.isArray(j.ticketSeenMap)) {
          setSupportTicketSeenMap(j.ticketSeenMap);
          if (typeof j.ticketsSeenAt === 'string' && j.ticketsSeenAt) setSupportSeenAt(j.ticketsSeenAt);
          try {
            localStorage.setItem('supportTicketSeenMap', JSON.stringify(j.ticketSeenMap));
            if (j.ticketsSeenAt) localStorage.setItem('supportTicketsSeenAt', j.ticketsSeenAt);
          } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const handleSelectSupportEmployee = useCallback((id) => {
    setSupportSelectedEmployeeId(id || '');
  }, []);

  // One-time cleanup: remove any legacy persisted selection from
  // previous builds so old installs don't restore it.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('supportSelectedEmployeeId');
    }
  }, []);

  const handleSetJonathanStatus = useCallback(async (status, availableUntil = undefined) => {
    try {
      const body = { staffId: 'jonathan', displayName: 'Jonathan', status };
      if (availableUntil !== undefined) body.availableUntil = availableUntil;
      await authFetch('/api/support/presence-set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setJonathanStatus(status);
      if (availableUntil !== undefined) setJonathanAvailableUntil(availableUntil);
      if (status === 'offline') setJonathanAvailableUntil(null);
    } catch {
      // ignore
    }
  }, []);

  const fdSupportEmployeesList = useMemo(() => {
    const list = Array.isArray(supportEmployees) ? supportEmployees : [];
    return list.filter((e) => e.active !== false);
  }, [supportEmployees]);

  return {
    supportCategories,
    setSupportCategories,
    supportEmployees,
    setSupportEmployees,
    fdSupportEmployeesList,
    supportSelectedEmployeeId,
    handleSelectSupportEmployee,
    jonathanStatus,
    jonathanAvailableUntil,
    handleSetJonathanStatus,
    supportMyTickets,
    supportSeenAt,
    supportTicketSeenMap,
    supportUnreadCount,
    handleMarkSupportSeen,
    handleMarkTicketSeen,
  };
}
